import {
    AlertColor,
    Avatar, Button,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Divider, Stack,
    Typography
} from "@mui/material";
import {Alert, LoadingButton} from "@mui/lab";
import React, {useEffect, useState} from "react";
import {getDriver, getUser, getUsers, updateDriverStatus} from "../../api/user";
import {useAppSelector} from "../../hooks/useRedux";
import LoadingScreen from "../../components/LoadingScreen";
import ErrorComponent from "../../components/ErrorComponent";
import {User} from "../../types/user";
import {DriverFormData} from "../../types/Driver";
import {H6} from "../../components/Typography";


type DriverDetailsDialogProps = {
    open?: boolean,
    onClose?: () => void,
    driver?: DriverFormData,
    driverId?: string,
    title?: string
}

const DriverDetailsDialog = (props: DriverDetailsDialogProps) => {
    const [decision, setDecision] = useState<DriverFormData['status']>()
    const [updatingStatus, setUpdatingStatus] = useState(false)
    const [driver, setDriver] = useState(props.driver || {})
    const [loadingDriver, setLoadingDriver] = useState<boolean>(false)
    const [loadingDriverFailed, setLoadingDriverFailed] = useState<boolean>(false)
    const driverStatusMap: { [key in DriverFormData['status']]: string } = {
        'PENDING': "En Cours",
        "VERIFIED": "Approuvé",
        "REJECTED": "Rejeté",
    }

    const driverStatusSeverityMap: { [key in DriverFormData['status']]: AlertColor } = {
        'PENDING': "warning",
        "VERIFIED": "success",
        "REJECTED": "error",
    }

    const driverGenderMap: { [key in DriverFormData['gender']]: string } = {
        'male': "Masculin",
        "female": "Féminin",
    }

    const loadDriver = async () => {
        try {
            setLoadingDriver(true)
            const driverResponse = await getDriver(driver.id || props.driverId)
            setDriver(driverResponse.data)
            setLoadingDriver(false)
            setLoadingDriverFailed(false)
        } catch (e) {
            setLoadingDriver(false)
            setLoadingDriverFailed(true)
            console.log(e)
        }
    }

    const handleUpdateStatus = async () => {
        setUpdatingStatus(true)
        await updateDriverStatus(driver.id, decision)
        setUpdatingStatus(false)
        loadDriver()
    }

    useEffect(() => {
        if (!props.driver && props.driverId)
            loadDriver()
    }, [])

    useEffect(() => {
        if (decision)
            handleUpdateStatus()
    }, [decision])

    if (!props.open)
        return null

    return (
        <Dialog
            fullWidth
            open={props.open}
            onClose={() => {
                if (!updatingStatus)
                    return props.onClose()
            }}>
            <DialogTitle
                sx={{fontSize: "large", textTransform: "capitalize"}}>{props.title || "Infos Conducteur"}</DialogTitle>
            <Divider/>
            <DialogContent>
                {loadingDriver ?
                    <LoadingScreen/> :
                    loadingDriverFailed ?
                        <ErrorComponent message={"Unable to load users"} onRetry={loadDriver}/> :
                        <Stack gap={1.5} justifyContent={"center"} alignItems={"center"}>

                            <Stack gap={1}>
                                <H6>Permis de Conduire</H6>
                                <img alt={"Driving License Front Image"}
                                     src={driver?.drivingLicenseImg?.front.uri}
                                     style={{
                                         objectFit: "contain",
                                         borderRadius: 12,
                                         width: "auto",
                                         height: 150,
                                     }}/>

                                <img alt={"Driving License Back Image"}
                                     src={driver?.drivingLicenseImg?.back.uri}
                                     style={{
                                         objectFit: "contain",
                                         borderRadius: 12,
                                         width: "auto",
                                         height: 150,
                                     }}/>
                            </Stack>


                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    ID Utilisateur:
                                </Typography>

                                <Typography>
                                    {driver.userId}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    ID Conducteur:
                                </Typography>

                                <Typography>
                                    {driver.id}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Nom Conducteur:
                                </Typography>

                                <Typography>
                                    {driver.name}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Date de Naissance:
                                </Typography>

                                <Typography maxWidth={"50%"}
                                            sx={{flexWrap: "wrap", textOverflow: "ellipsis", maxLines: 1}}>
                                    {new Date(driver.dateOfBirth).toLocaleDateString()}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Numéro de permis:
                                </Typography>

                                <Typography maxWidth={"50%"}
                                            sx={{flexWrap: "wrap", textOverflow: "ellipsis", maxLines: 1}}>
                                    {driver.drivingLicenseId}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Date de délivrance du permis:
                                </Typography>

                                <Typography maxWidth={"50%"}
                                            sx={{flexWrap: "wrap", textOverflow: "ellipsis", maxLines: 1}}>
                                    {new Date(driver.drivingLicenseExpiry?.issued).toLocaleDateString()}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Date d'expiration du permis:
                                </Typography>

                                <Typography maxWidth={"50%"}
                                            sx={{flexWrap: "wrap", textOverflow: "ellipsis", maxLines: 1}}>
                                    {new Date(driver.drivingLicenseExpiry?.expires).toLocaleDateString()}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Modèle de voiture:
                                </Typography>

                                <Typography maxWidth={"50%"}
                                            sx={{flexWrap: "wrap", textOverflow: "ellipsis", maxLines: 1}}>
                                    {driver.carName}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Plaque d'immatriculation:
                                </Typography>

                                <Typography maxWidth={"50%"}
                                            sx={{flexWrap: "wrap", textOverflow: "ellipsis", maxLines: 1}}>
                                    {driver.licensePlate}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Numéro d'urgence:
                                </Typography>

                                <Typography>
                                    {driver.emergencyContact}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Sexe:
                                </Typography>

                                <Typography>
                                    {driverGenderMap[driver.gender]}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Date de Creation:
                                </Typography>

                                <Typography>
                                    {new Date(driver['createdAt']).toDateString()}
                                </Typography>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Statut de la requête:
                                </Typography>

                                <Alert severity={driverStatusSeverityMap[driver?.status]}
                                       variant={"outlined"}
                                       color={driverStatusSeverityMap[driver?.status]}
                                       sx={{paddingY: 0, paddingX: 1, fontSize: 13, textTransform: "capitalize"}}>
                                    {driverStatusMap[driver.status]}
                                </Alert>
                            </Stack>

                            <Stack direction={"row"} width={"100%"} justifyContent={"space-between"}
                                   alignItems={"center"}>
                                <Typography fontWeight={"bold"}>
                                    Décision:
                                </Typography>

                                <Stack gap={2} alignItems={"center"} direction={"row"}>
                                    <LoadingButton loading={decision === "VERIFIED" && updatingStatus}
                                                   disabled={driver.status === "VERIFIED" || (decision === "REJECTED" && updatingStatus)}
                                                   onClick={() => setDecision("VERIFIED")}
                                                   variant={"contained"} color={"success"} style={{color: "white"}}>
                                        Approuver
                                    </LoadingButton>

                                    <LoadingButton loading={decision === "REJECTED" && updatingStatus}
                                                   disabled={driver.status === "REJECTED" || (decision === "VERIFIED" && updatingStatus)}
                                                   onClick={() => setDecision("REJECTED")}
                                                   variant={"contained"} color={"error"} style={{color: "white"}}>
                                        Rejeter
                                    </LoadingButton>
                                </Stack>
                            </Stack>
                        </Stack>
                }</DialogContent>
            <Divider/>
            <DialogActions>
                <Button variant={"text"} disabled={updatingStatus} onClick={props.onClose}>
                    Terminer
                </Button>
            </DialogActions>
        </Dialog>
    )

}

export default DriverDetailsDialog